import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(''); // Changed from 'services' to empty string
    const [hasAnimated, setHasAnimated] = useState(false);
    const [isNavbarVisible, setIsNavbarVisible] = useState(false); // New state to control navbar visibility
    const [showFloatingButton, setShowFloatingButton] = useState(true); // State for floating button
    const location = useLocation();

    // Trigger navbar entrance animation on component mount
    useEffect(() => {
        const timer = setTimeout(() => {
            setHasAnimated(true);
        }, 200);
        return () => clearTimeout(timer);
    }, []);

    // Show navbar only when mobile menu button is clicked or when not on mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                // Show navbar on desktop/tablet, hide floating button
                setIsNavbarVisible(true);
                setShowFloatingButton(false);
            } else {
                // Hide navbar on mobile unless mobile menu is open, show floating button
                setIsNavbarVisible(isMobileMenuOpen);
                setShowFloatingButton(!isMobileMenuOpen);
            }
        };

        // Initial check
        handleResize();

        // Add resize listener
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileMenuOpen]);

    // Clear active section when navigating away from home page
    useEffect(() => {
        if (location.pathname !== '/') {
            setActiveSection('');
        }
    }, [location.pathname]);
    
    // Handle click outside to close mobile menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Only handle mouse clicks, completely ignore touch events
            if (event.type.includes('touch')) {
                return;
            }
            
            // Don't close menu if click is from carousel or any carousel-related element
            if (event.target.closest('[data-carousel="true"]') || 
                event.target.closest('[data-no-navbar-touch="true"]') ||
                event.target.closest('.carousel-container') ||
                event.target.closest('.carousel-video-wrapper') ||
                event.target.closest('video')) {
                return;
            }
            
            if (isMobileMenuOpen && !event.target.closest('.navbar-nav') && !event.target.closest('.mobile-menu-btn')) {
                setIsMobileMenuOpen(false);
                if (window.innerWidth < 768) {
                    setIsNavbarVisible(false);
                    setShowFloatingButton(true);
                }
            }
        };

        // Only add mouse event listener, NO touch events
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    // Completely disable swipe gestures for navbar on mobile
    useEffect(() => {
        const preventSwipeGestures = (event) => {
            // Prevent any swipe-based navbar activation
            if (event.target.closest('body') && !event.target.closest('.floating-menu-btn')) {
                // Allow only explicit button clicks for navbar
                return;
            }
        };

        // Disable any potential swipe-to-open functionality
        const disableSwipe = { passive: true };
        document.addEventListener('touchstart', preventSwipeGestures, disableSwipe);
        document.addEventListener('touchmove', preventSwipeGestures, disableSwipe);
        document.addEventListener('touchend', preventSwipeGestures, disableSwipe);

        return () => {
            document.removeEventListener('touchstart', preventSwipeGestures);
            document.removeEventListener('touchmove', preventSwipeGestures);
            document.removeEventListener('touchend', preventSwipeGestures);
        };
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        // On mobile, show/hide navbar when mobile menu is toggled
        if (window.innerWidth < 768) {
            setIsNavbarVisible(!isMobileMenuOpen);
            setShowFloatingButton(isMobileMenuOpen); // Hide floating button when menu opens
        }
    }; 
    
    const handleNavClick = (section) => {
        console.log(`Navigating to ${section}`);
        // Close mobile menu when nav item is clicked
        setIsMobileMenuOpen(false);

        // Set active section
        setActiveSection(section);

        // Only scroll to section if we're on the home page
        if (location.pathname === '/') {
            const sectionMap = {
                'testimonial': 'team-section', // Navigate to the Meet Our Team section
                'services': 'project-showcase', // Services should go to project showcase
                'join-us': 'contact-form' // Join us should go to contact form
            };

            const targetId = sectionMap[section];
            if (targetId) {
                const element = document.getElementById(targetId);
                if (element) {
                    // Adjust scroll offset based on section type
                    let navbarHeight = 100; // Fixed navbar height
                    let additionalOffset = 20; // Default additional offset

                    const offsetTop = element.offsetTop - navbarHeight - additionalOffset;

                    window.scrollTo({
                        top: offsetTop,
                        behavior: 'smooth'
                    });
                } else {
                    console.warn(`Element with ID '${targetId}' not found`);
                }
            }
        }
    };
    
    return (
        <>
            {/* Floating Menu Button for Mobile (when navbar is hidden) */}
            {showFloatingButton && (
                <button
                    className="floating-menu-btn fixed top-4 right-4 z-[1102] w-12 h-12 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
                    onClick={toggleMobileMenu}
                    aria-label="Open menu"
                    style={{ 
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation' // Only allow tap, no swipe
                    }}
                >
                    <span className="w-6 h-6 flex flex-col justify-center space-y-1">
                        <span className="w-full h-0.5 bg-gold block"></span>
                        <span className="w-full h-0.5 bg-gold block"></span>
                        <span className="w-full h-0.5 bg-gold block"></span>
                    </span>
                </button>
            )}

        <header
            className={`navbar-container bg-primary ${isMobileMenuOpen ? 'mobile-menu-open' : ''} transition-all duration-1000 ease-out ${hasAnimated && isNavbarVisible ? 'opacity-100 transform-none' : 'opacity-0 -translate-y-full'}`}
            style={{
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transform: (hasAnimated && isNavbarVisible) ? 'translateY(0)' : 'translateY(-100%)',
                transformOrigin: 'top center',
                display: isNavbarVisible ? 'block' : 'none'
            }}
        >
            <div className="navbar-layout">

                {/* Logo */}
                <div className="navbar-logo-container">
                    <Link to="/">
                        <img
                            src="/images/mmlconcepts_logo.png"
                            alt="Company Logo"
                            className="navbar-logo"
                        />
                    </Link>
                </div>                {/* Mobile Menu Button */}
                <button
                    className={`mobile-menu-btn transition-all duration-500 ${hasAnimated ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-4 scale-90'} ${isMobileMenuOpen ? 'is-active' : ''}`}
                    onClick={toggleMobileMenu}
                    onFocus={(e) => e.currentTarget.blur()} /* Prevent focus outline */
                    aria-label="Toggle mobile menu"
                    aria-expanded={isMobileMenuOpen}
                    style={{ 
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation' // Only allow tap, no swipe
                    }}
                >
                    <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
                    <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
                    <span className={`hamburger-line ${isMobileMenuOpen ? 'active' : ''}`}></span>
                </button>

                {/* Navigation */}
                <nav className={`navbar-nav ${isMobileMenuOpen ? 'mobile-open' : ''} transition-all duration-800 delay-600 ${hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                    {location.pathname === '/' ? (
                        <button
                            className={`navbar-btn ${activeSection === 'testimonial' ? 'active' : ''} transition-all duration-600 delay-800 ${hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                            onClick={() => handleNavClick('testimonial')}
                        >
                            Testimonial
                        </button>
                    ) : (
                        <Link
                            to="/"
                            className={`navbar-btn transition-all duration-600 delay-800 ${hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Home
                        </Link>
                    )}
                    {location.pathname === '/' ? (
                        <button
                            className={`navbar-btn ${activeSection === 'services' ? 'active' : ''} transition-all duration-600 delay-900 ${hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                            onClick={() => handleNavClick('services')}
                        >
                            Services
                        </button>
                    ) : (
                        <Link
                            to="/"
                            className={`navbar-btn transition-all duration-600 delay-900 ${hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Services
                        </Link>
                    )}
                    <div className={`join-us-container transition-all duration-600 delay-1000 ${hasAnimated ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-3 scale-95'}`}>
                        <div className="join-us-shadow"></div>
                        {location.pathname === '/' ? (
                            <button
                                className={`join-us-btn ${activeSection === 'join-us' ? 'active' : ''}`}
                                onClick={() => handleNavClick('join-us')}
                            >
                                Join Us
                            </button>
                        ) : (
                            <Link
                                to="/contact"
                                className={`join-us-btn ${location.pathname === '/contact' ? 'active' : ''}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Contact
                            </Link>
                        )}
                    </div>
                </nav>
            </div>
        </header>
        </>
    );
};

export default Navbar;
